/**
 * DBN Programme of Activities (PoA) — Carbon & Environmental Credits Engine
 * Part of Environmental Markets & Monetisation workspace
 */
import React, { useState, useEffect, useCallback } from "react";
import {
  AreaChart, Area, BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from "recharts";
import { dbnApi, SectionHeader, Badge, LoadingState, Btn, fmtEur, fmtN, ProgressBar, scoreColor, Modal } from "./dbnUtils";
import { Leaf, TrendingUp, Award, CheckCircle, AlertTriangle, ChevronRight, Search, Plus, FileText, Zap, Bot } from "lucide-react";

// ── Status colours consistent with platform ───────────────────────────────────
const STATUS_COLORS = {
  Draft:"#475569", Aggregating:"#a78bfa", Validation:"#f59e0b",
  Registered:"#3b82f6", Monitoring:"#60a5fa", Verification:"#f97316",
  Issuing:"#22c55e", Trading:"#0d9488", Closed:"#334155",
};
const TYPE_COLORS = { Carbon:"#34d399", Plastic:"#60a5fa", "Carbon + Plastic":"#a78bfa" };

// ── PoA Dashboard ─────────────────────────────────────────────────────────────
function PoADashboard({ overview }) {
  const k = overview.kpis || {};
  const trend = overview.issuance_trend || [];
  const byStatus = Object.entries(overview.by_status||{}).map(([s,v]) => ({ name:s, value:v, fill:STATUS_COLORS[s]||"#475569" }));

  return (
    <div>
      {/* 8 KPI cards */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:10, marginBottom:12 }}>
        {[
          {l:"Active PoAs",          v:k.active_poas,                                     c:"#22c55e"},
          {l:"Component Projects",    v:k.total_cpas,                                      c:"#34d399"},
          {l:"Annual tCO₂e",         v:fmtN(k.annual_tco2e),                              c:"#0d9488"},
          {l:"Annual Plastic Credits",v:fmtN(k.plastic_credits_annual||0),                 c:"#60a5fa"},
          {l:"Credits Issued",        v:fmtN(k.credits_issued),                            c:"#fbbf24"},
          {l:"Credits Sold",          v:fmtN(k.credits_sold),                              c:"#f97316"},
          {l:"Revenue Realised",      v:fmtEur(k.revenue_realised),                        c:"#22c55e"},
          {l:"Revenue Pipeline",      v:fmtEur(k.revenue_pipeline),                        c:"#a78bfa"},
        ].map(x => (
          <div key={x.l} style={{ background:"#0a1628", border:"1px solid #0f2033",
            borderTop:`3px solid ${x.c}`, borderRadius:12, padding:"12px 14px" }}>
            <p style={{ fontSize:18, fontWeight:900, color:x.c }}>{x.v}</p>
            <p style={{ fontSize:9, color:"#475569", textTransform:"uppercase", letterSpacing:"0.07em" }}>{x.l}</p>
          </div>
        ))}
      </div>

      {/* Secondary KPIs */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:10, marginBottom:16 }}>
        {[
          {l:"Draft/Aggregating PoAs",  v:k.draft_poas,               c:"#a78bfa"},
          {l:"Registered PoAs",          v:k.registered_poas,           c:"#3b82f6"},
          {l:"States Covered",           v:k.states_covered,            c:"#60a5fa"},
          {l:"Aggregation Opportunities",v:k.aggregation_opportunities,  c:"#f59e0b"},
        ].map(x => (
          <div key={x.l} style={{ background:"#0a1628", border:"1px solid #0f2033",
            borderLeft:`3px solid ${x.c}`, borderRadius:10, padding:"10px 14px" }}>
            <p style={{ fontSize:16, fontWeight:900, color:x.c }}>{x.v}</p>
            <p style={{ fontSize:9, color:"#475569", textTransform:"uppercase", letterSpacing:"0.07em" }}>{x.l}</p>
          </div>
        ))}
      </div>

      {/* Charts */}
      <div style={{ display:"grid", gridTemplateColumns:"1.4fr 1fr 1fr", gap:14 }}>
        {/* Issuance trend */}
        <div style={{ background:"#0a1628", border:"1px solid #0f2033", borderRadius:14, padding:14 }}>
          <SectionHeader title="Issuance & Revenue Trend" sub="Quarterly across all PoAs" />
          <ResponsiveContainer width="100%" height={180}>
            <AreaChart data={trend.filter(t=>t.issued)} margin={{ top:4, right:8, bottom:4, left:8 }}>
              <defs>
                <linearGradient id="ig1" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#34d399" stopOpacity={0.25}/>
                  <stop offset="95%" stopColor="#34d399" stopOpacity={0}/>
                </linearGradient>
                <linearGradient id="ig2" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#fbbf24" stopOpacity={0.25}/>
                  <stop offset="95%" stopColor="#fbbf24" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid stroke="#0f2033" strokeDasharray="3 3" />
              <XAxis dataKey="quarter" tick={{ fill:"#475569", fontSize:8 }} />
              <YAxis tick={{ fill:"#475569", fontSize:8 }} />
              <Tooltip contentStyle={{ background:"#0a1628", border:"1px solid #1e3a5f", borderRadius:8, fontSize:9 }} />
              <Area type="monotone" dataKey="issued" name="Issued" stroke="#34d399" fill="url(#ig1)" strokeWidth={2} />
              <Area type="monotone" dataKey="sold" name="Sold" stroke="#fbbf24" fill="url(#ig2)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Status distribution */}
        <div style={{ background:"#0a1628", border:"1px solid #0f2033", borderRadius:14, padding:14 }}>
          <SectionHeader title="Programme Status" sub="All PoAs" />
          <ResponsiveContainer width="100%" height={140}>
            <PieChart>
              <Pie data={byStatus} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={55}>
                {byStatus.map((e,i) => <Cell key={i} fill={e.fill} />)}
              </Pie>
              <Tooltip contentStyle={{ background:"#0a1628", border:"1px solid #1e3a5f", fontSize:9 }} />
            </PieChart>
          </ResponsiveContainer>
          <div style={{ display:"flex", flexWrap:"wrap", gap:4, marginTop:6 }}>
            {byStatus.map(e => (
              <span key={e.name} style={{ fontSize:8, background:`${e.fill}20`, color:e.fill,
                padding:"1px 6px", borderRadius:4, fontWeight:700 }}>
                {e.name}: {e.value}
              </span>
            ))}
          </div>
        </div>

        {/* Top programmes */}
        <div style={{ background:"#0a1628", border:"1px solid #0f2033", borderRadius:14, padding:14 }}>
          <SectionHeader title="Top Programmes" sub="By annual tCO₂e" />
          {(overview.top_programmes||[]).map(p => (
            <div key={p.id} style={{ marginBottom:10 }}>
              <div style={{ display:"flex", justifyContent:"space-between", marginBottom:3 }}>
                <span style={{ fontSize:9, color:"#94a3b8", fontWeight:600 }}>{p.name.slice(0,28)}</span>
                <span style={{ fontSize:9, color:STATUS_COLORS[p.status]||"#475569", fontWeight:700 }}>
                  {p.status}
                </span>
              </div>
              <ProgressBar value={p.annual_reductions_tco2e} max={60000}
                color={STATUS_COLORS[p.status]||"#34d399"} height={5} showPct={false} />
              <p style={{ fontSize:8, color:"#334155", marginTop:2 }}>
                {fmtN(p.annual_reductions_tco2e)} tCO₂e/yr · {fmtEur(p.revenue_realised)} realised
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Programme Portfolio Table ─────────────────────────────────────────────────
function ProgrammePortfolio({ programmes, onSelect }) {
  const [search, setSearch] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");

  const filtered = (programmes||[]).filter(p =>
    (filterType==="all" || p.credit_type===filterType) &&
    (filterStatus==="all" || p.status===filterStatus) &&
    (search==="" || p.name.toLowerCase().includes(search.toLowerCase()) || p.registry.toLowerCase().includes(search.toLowerCase()))
  );

  const statuses = [...new Set((programmes||[]).map(p=>p.status))];
  const types    = [...new Set((programmes||[]).map(p=>p.credit_type))];

  return (
    <div>
      {/* Filters */}
      <div style={{ display:"flex", gap:8, marginBottom:14, flexWrap:"wrap" }}>
        <div style={{ display:"flex", alignItems:"center", gap:6, background:"#0a1628",
          border:"1px solid #0f2033", borderRadius:8, padding:"5px 10px" }}>
          <Search size={11} color="#475569" />
          <input value={search} onChange={e=>setSearch(e.target.value)}
            placeholder="Search programmes…"
            style={{ background:"none", border:"none", color:"#f0f9ff", fontSize:10, outline:"none", width:160 }} />
        </div>
        {[
          {val:filterType,  set:setFilterType,  opts:[{v:"all",l:"All Types"},...types.map(t=>({v:t,l:t}))]},
          {val:filterStatus,set:setFilterStatus,opts:[{v:"all",l:"All Status"},...statuses.map(s=>({v:s,l:s}))]},
        ].map((f,i) => (
          <select key={i} value={f.val} onChange={e=>f.set(e.target.value)}
            style={{ background:"#0a1628", border:"1px solid #0f2033", borderRadius:8,
              padding:"5px 10px", fontSize:10, color:"#94a3b8" }}>
            {f.opts.map(o=><option key={o.v} value={o.v}>{o.l}</option>)}
          </select>
        ))}
        <span style={{ fontSize:10, color:"#475569", alignSelf:"center" }}>{filtered.length} programmes</span>
      </div>

      {/* Table */}
      <div style={{ background:"#0a1628", border:"1px solid #0f2033", borderRadius:14, overflow:"hidden" }}>
        <table style={{ width:"100%", borderCollapse:"collapse" }}>
          <thead>
            <tr>
              {["Programme","Type","Methodology","Registry","Status","CPAs","Annual tCO₂e","Credits Issued","Revenue","Article 6"].map(h => (
                <th key={h} style={{ background:"#060f1e", color:"#475569", fontSize:8.5, fontWeight:700,
                  padding:"8px 10px", textAlign:"left", textTransform:"uppercase", letterSpacing:"0.07em" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.map(p => (
              <tr key={p.id} onClick={() => onSelect(p.id)}
                style={{ cursor:"pointer" }}
                onMouseEnter={e=>e.currentTarget.style.background="rgba(52,211,153,0.05)"}
                onMouseLeave={e=>e.currentTarget.style.background=""}>
                <td style={{ padding:"8px 10px", borderBottom:"1px solid #0f1e33" }}>
                  <p style={{ fontSize:10, fontWeight:700, color:"#f0f9ff" }}>{p.name}</p>
                  <p style={{ fontSize:8, color:"#475569" }}>{p.id} · {p.states?.slice(0,2).join(", ")}{(p.states?.length||0)>2?"…":""}</p>
                </td>
                <td style={{ padding:"8px 10px", borderBottom:"1px solid #0f1e33" }}>
                  <Badge label={p.credit_type} color={TYPE_COLORS[p.credit_type]||"#475569"} />
                </td>
                <td style={{ padding:"8px 10px", fontSize:8.5, color:"#64748b", borderBottom:"1px solid #0f1e33", maxWidth:120 }}>
                  {p.methodology.slice(0,30)}…
                </td>
                <td style={{ padding:"8px 10px", fontSize:9, color:"#94a3b8", borderBottom:"1px solid #0f1e33" }}>{p.registry}</td>
                <td style={{ padding:"8px 10px", borderBottom:"1px solid #0f1e33" }}>
                  <Badge label={p.status} color={STATUS_COLORS[p.status]||"#475569"} />
                </td>
                <td style={{ padding:"8px 10px", fontSize:10, fontWeight:700, color:"#60a5fa", borderBottom:"1px solid #0f1e33", textAlign:"center" }}>
                  {p.cpa_count}
                </td>
                <td style={{ padding:"8px 10px", fontSize:10, color:"#34d399", fontWeight:700, borderBottom:"1px solid #0f1e33" }}>
                  {fmtN(p.annual_reductions_tco2e)}
                </td>
                <td style={{ padding:"8px 10px", fontSize:10, color:"#fbbf24", fontWeight:700, borderBottom:"1px solid #0f1e33" }}>
                  {fmtN(p.credits_issued)}
                </td>
                <td style={{ padding:"8px 10px", fontSize:10, color:"#22c55e", fontWeight:700, borderBottom:"1px solid #0f1e33" }}>
                  {fmtEur(p.revenue_realised)}
                </td>
                <td style={{ padding:"8px 10px", borderBottom:"1px solid #0f1e33", textAlign:"center" }}>
                  {p.article6_ready ? <span style={{ color:"#22c55e", fontSize:10 }}>✓</span> : <span style={{ color:"#334155", fontSize:10 }}>–</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Programme Detail Drawer ───────────────────────────────────────────────────
function ProgrammeDetail({ poaId, onClose }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("overview");
  const [aiContent, setAiContent] = useState(null);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    if (poaId) {
      setLoading(true);
      dbnApi.poaProgramme(poaId)
        .then(r => setDetail(r.data))
        .catch(() => {})
        .finally(() => setLoading(false));
    }
  }, [poaId]);

  const generateAi = async () => {
    setGenerating(true);
    try {
      const r = await dbnApi.poaAi({ workflow:"programme_detail", poa_id:poaId });
      setAiContent(r.data?.content);
    } catch(e) {}
    setGenerating(false);
  };

  if (!poaId) return null;

  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,.75)", zIndex:2000,
      display:"flex", alignItems:"flex-start", justifyContent:"flex-end" }}
      onClick={onClose}>
      <div style={{ width:"100%", maxWidth:680, height:"100%", background:"#0a1628",
        border:"1px solid #1e3a5f", overflowY:"auto", boxShadow:"-8px 0 40px rgba(0,0,0,.6)" }}
        onClick={e=>e.stopPropagation()}>
        {loading ? <LoadingState /> : (
          <>
            {/* Header */}
            <div style={{ background:"#07111e", borderBottom:"1px solid #0f2033", padding:"14px 20px",
              display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
              <div>
                <p style={{ fontSize:14, fontWeight:800, color:"#f0f9ff" }}>{detail?.programme?.name}</p>
                <div style={{ display:"flex", gap:6, marginTop:4 }}>
                  <Badge label={detail?.programme?.credit_type||""} color={TYPE_COLORS[detail?.programme?.credit_type]||"#475569"} />
                  <Badge label={detail?.programme?.status||""} color={STATUS_COLORS[detail?.programme?.status]||"#475569"} />
                  <Badge label={detail?.programme?.registry||""} color="#334155" />
                  {detail?.programme?.article6_ready && <Badge label="Article 6 Ready" color="#22c55e" />}
                </div>
              </div>
              <button onClick={onClose} style={{ background:"none", border:"none", color:"#475569", cursor:"pointer", fontSize:18 }}>✕</button>
            </div>

            {/* Tabs */}
            <div style={{ display:"flex", borderBottom:"1px solid #0f2033", overflowX:"auto" }}>
              {["overview","cpas","mrv","registry","revenue","ai"].map(t => (
                <button key={t} onClick={() => setTab(t)}
                  style={{ padding:"8px 14px", border:"none",
                    borderBottom:`2px solid ${tab===t?"#34d399":"transparent"}`,
                    background:"transparent", color:tab===t?"#34d399":"#475569",
                    fontSize:10, fontWeight:700, cursor:"pointer", textTransform:"capitalize", whiteSpace:"nowrap" }}>
                  {t === "ai" ? "AI Insights" : t === "cpas" ? "Component Projects" :
                   t === "mrv" ? "MRV" : t === "registry" ? "Registry & Issuance" : t}
                </button>
              ))}
            </div>

            <div style={{ padding:"16px 20px" }}>
              {/* Overview */}
              {tab === "overview" && (
                <div>
                  <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:8, marginBottom:14 }}>
                    {[
                      ["Annual tCO₂e",    fmtN(detail?.programme?.annual_reductions_tco2e),  "#34d399"],
                      ["Credits Issued",   fmtN(detail?.programme?.credits_issued),            "#fbbf24"],
                      ["Credits Sold",     fmtN(detail?.programme?.credits_sold),              "#f97316"],
                      ["Credits Retired",  fmtN(detail?.programme?.credits_retired),           "#475569"],
                      ["Revenue Realised", fmtEur(detail?.programme?.revenue_realised),        "#22c55e"],
                      ["Revenue Pipeline", fmtEur(detail?.programme?.revenue_pipeline),        "#a78bfa"],
                    ].map(([l,v,c]) => (
                      <div key={l} style={{ background:"#060f1e", borderRadius:8, padding:"8px 12px", borderLeft:`3px solid ${c}` }}>
                        <p style={{ fontSize:13, fontWeight:900, color:c }}>{v}</p>
                        <p style={{ fontSize:8, color:"#475569", textTransform:"uppercase" }}>{l}</p>
                      </div>
                    ))}
                  </div>
                  <div style={{ background:"#060f1e", borderRadius:10, padding:"12px 14px", marginBottom:10 }}>
                    <p style={{ fontSize:11, fontWeight:700, color:"#f0f9ff", marginBottom:6 }}>Programme Description</p>
                    <p style={{ fontSize:10, color:"#64748b", lineHeight:1.7 }}>{detail?.programme?.description}</p>
                  </div>
                  <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8 }}>
                    {[
                      ["Methodology", detail?.programme?.methodology],
                      ["Registry ID", detail?.programme?.registry_id || "Pending registration"],
                      ["Verifier", detail?.programme?.verifier],
                      ["Validation Date", detail?.programme?.validation_date || "—"],
                      ["Registration Date", detail?.programme?.registration_date || "—"],
                      ["Next Verification", detail?.programme?.next_verification || "—"],
                      ["CPAs", detail?.programme?.cpa_count],
                      ["States", detail?.programme?.states?.join(", ")],
                    ].map(([l,v]) => (
                      <div key={l} style={{ background:"#060f1e", borderRadius:6, padding:"7px 10px" }}>
                        <p style={{ fontSize:8, color:"#475569", marginBottom:1 }}>{l}</p>
                        <p style={{ fontSize:10, fontWeight:600, color:"#f0f9ff" }}>{v||"—"}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Component Projects (CPAs) */}
              {tab === "cpas" && (
                <div>
                  <p style={{ fontSize:10, color:"#475569", marginBottom:12 }}>
                    {detail?.cpas?.length} Component Project Activities enrolled in this programme.
                  </p>
                  {(detail?.cpas||[]).map(cpa => (
                    <div key={cpa.id} style={{ background:"#060f1e", borderRadius:10, padding:"10px 14px", marginBottom:8 }}>
                      <div style={{ display:"flex", justifyContent:"space-between", marginBottom:6 }}>
                        <div>
                          <p style={{ fontSize:11, fontWeight:700, color:"#f0f9ff" }}>{cpa.name}</p>
                          <p style={{ fontSize:8.5, color:"#475569" }}>{cpa.id} · {cpa.sector} · {cpa.state} · {cpa.technology}</p>
                        </div>
                        <div style={{ textAlign:"right" }}>
                          <p style={{ fontSize:10, fontWeight:700, color:"#34d399" }}>{fmtN(cpa.annual_reductions_tco2e)} tCO₂e/yr</p>
                          <Badge label={cpa.verification_status} color={cpa.verification_status==="Verified"?"#22c55e":cpa.verification_status==="In Review"?"#f59e0b":"#475569"} />
                        </div>
                      </div>
                      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr 1fr", gap:6 }}>
                        {[
                          ["Baseline Emissions",`${fmtN(cpa.baseline_emissions_tco2e)} tCO₂e`],
                          ["Project Emissions", `${fmtN(cpa.project_emissions_tco2e)} tCO₂e`],
                          ["Annual Reduction",  `${fmtN(cpa.annual_reductions_tco2e)} tCO₂e`],
                          ["Monitoring Status", cpa.monitoring_status],
                        ].map(([l,v]) => (
                          <div key={l} style={{ background:"#0a1628", borderRadius:5, padding:"4px 8px" }}>
                            <p style={{ fontSize:7.5, color:"#334155" }}>{l}</p>
                            <p style={{ fontSize:9, fontWeight:700, color:"#94a3b8" }}>{v}</p>
                          </div>
                        ))}
                      </div>
                      <div style={{ marginTop:6 }}>
                        <div style={{ display:"flex", justifyContent:"space-between", marginBottom:2 }}>
                          <span style={{ fontSize:8, color:"#475569" }}>Evidence completeness</span>
                          <span style={{ fontSize:9, fontWeight:700, color:scoreColor(cpa.evidence_completeness_pct) }}>
                            {cpa.evidence_completeness_pct}%
                          </span>
                        </div>
                        <ProgressBar value={cpa.evidence_completeness_pct} color={scoreColor(cpa.evidence_completeness_pct)} height={4} showPct={false} />
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* MRV */}
              {tab === "mrv" && detail?.mrv && (
                <div>
                  <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:8, marginBottom:14 }}>
                    {[
                      ["MRV Completeness",    `${detail.mrv.completeness_pct}%`,     scoreColor(detail.mrv.completeness_pct)],
                      ["Evidence Submitted",  detail.mrv.evidence_submitted,          "#34d399"],
                      ["Evidence Missing",    detail.mrv.evidence_missing,            detail.mrv.evidence_missing>0?"#f59e0b":"#22c55e"],
                    ].map(([l,v,c]) => (
                      <div key={l} style={{ background:"#060f1e", borderRadius:10, padding:"10px 12px", borderLeft:`3px solid ${c}` }}>
                        <p style={{ fontSize:18, fontWeight:900, color:c }}>{v}</p>
                        <p style={{ fontSize:8, color:"#475569", textTransform:"uppercase" }}>{l}</p>
                      </div>
                    ))}
                  </div>
                  <div style={{ background:"#060f1e", borderRadius:10, padding:"12px 14px", marginBottom:12 }}>
                    <ProgressBar value={detail.mrv.completeness_pct} color={scoreColor(detail.mrv.completeness_pct)} height={8} showPct={false} />
                    <div style={{ display:"flex", justifyContent:"space-between", marginTop:6 }}>
                      <span style={{ fontSize:9, color:"#475569" }}>Monitoring period: {detail.mrv.monitoring_period}</span>
                      <span style={{ fontSize:9, color:"#475569" }}>Verifier: {detail.mrv.verifier}</span>
                    </div>
                    <div style={{ display:"flex", justifyContent:"space-between", marginTop:4 }}>
                      <span style={{ fontSize:9, color:"#475569" }}>Reporting deadline: {detail.mrv.reporting_deadline}</span>
                      <span style={{ fontSize:9, color:"#f59e0b" }}>Next verification: {detail.mrv.next_verification||"—"}</span>
                    </div>
                  </div>
                  {(detail.mrv.qa_flags||[]).length > 0 && (
                    <div>
                      <p style={{ fontSize:10, fontWeight:700, color:"#f59e0b", marginBottom:8 }}>QA Flags</p>
                      {detail.mrv.qa_flags.map((f,i) => (
                        <div key={i} style={{ background:"#1a0a00", borderRadius:8, padding:"7px 12px",
                          borderLeft:`3px solid ${f.severity==="Medium"?"#f59e0b":"#60a5fa"}`, marginBottom:6 }}>
                          <p style={{ fontSize:9, color:f.severity==="Medium"?"#f59e0b":"#60a5fa", fontWeight:700 }}>{f.severity}</p>
                          <p style={{ fontSize:9, color:"#64748b", lineHeight:1.5 }}>{f.flag}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Registry & Issuance */}
              {tab === "registry" && (
                <div>
                  <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, marginBottom:14 }}>
                    {[
                      ["Registry",          detail?.programme?.registry],
                      ["Registry ID",       detail?.programme?.registry_id || "Pending registration"],
                      ["Methodology",       detail?.programme?.methodology?.split("/")?.[0]?.trim()],
                      ["Validation Date",   detail?.programme?.validation_date || "—"],
                      ["Registration Date", detail?.programme?.registration_date || "—"],
                      ["Verifier",          detail?.programme?.verifier],
                      ["Article 6 Ready",   detail?.programme?.article6_ready ? "Yes" : "No"],
                      ["Next Verification", detail?.programme?.next_verification || "—"],
                    ].map(([l,v]) => (
                      <div key={l} style={{ background:"#060f1e", borderRadius:6, padding:"7px 10px" }}>
                        <p style={{ fontSize:8, color:"#475569", marginBottom:1 }}>{l}</p>
                        <p style={{ fontSize:10, fontWeight:600, color:"#f0f9ff" }}>{v||"—"}</p>
                      </div>
                    ))}
                  </div>
                  {(detail?.issuance_records||[]).length > 0 && (
                    <div>
                      <p style={{ fontSize:11, fontWeight:700, color:"#f0f9ff", marginBottom:10 }}>Issuance History</p>
                      {detail.issuance_records.map((r,i) => (
                        <div key={i} style={{ background:"#060f1e", borderRadius:8, padding:"8px 12px", marginBottom:6,
                          borderLeft:`3px solid ${r.status==="Completed"?"#22c55e":"#f59e0b"}` }}>
                          <div style={{ display:"flex", justifyContent:"space-between", marginBottom:4 }}>
                            <p style={{ fontSize:10, fontWeight:700, color:"#f0f9ff" }}>{r.type}</p>
                            <Badge label={r.status} color={r.status==="Completed"?"#22c55e":"#f59e0b"} />
                          </div>
                          <p style={{ fontSize:9, color:"#64748b" }}>
                            {r.date} · {fmtN(r.quantity)} credits · {fmtEur(r.price)}/VCU · Revenue: {fmtEur(r.revenue)}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Revenue */}
              {tab === "revenue" && (
                <div>
                  <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, marginBottom:14 }}>
                    {[
                      ["Credits Issued",    fmtN(detail?.programme?.credits_issued),     "#fbbf24"],
                      ["Credits Sold",      fmtN(detail?.programme?.credits_sold),        "#f97316"],
                      ["Credits Unsold",    fmtN(detail?.programme?.credits_unsold),      "#a78bfa"],
                      ["Avg Price/VCU",     `€${detail?.programme?.avg_price_per_vcu}`,   "#34d399"],
                      ["Revenue Realised",  fmtEur(detail?.programme?.revenue_realised),  "#22c55e"],
                      ["Revenue Pipeline",  fmtEur(detail?.programme?.revenue_pipeline),  "#60a5fa"],
                    ].map(([l,v,c]) => (
                      <div key={l} style={{ background:"#060f1e", borderRadius:8, padding:"10px 12px", borderLeft:`3px solid ${c}` }}>
                        <p style={{ fontSize:16, fontWeight:900, color:c }}>{v}</p>
                        <p style={{ fontSize:8, color:"#475569", textTransform:"uppercase" }}>{l}</p>
                      </div>
                    ))}
                  </div>
                  <div style={{ background:"#001a0a", border:"1px solid #004a1e", borderRadius:10, padding:"10px 14px" }}>
                    <p style={{ fontSize:10, fontWeight:700, color:"#34d399", marginBottom:4 }}>Revenue Projection</p>
                    <p style={{ fontSize:10, color:"#065f46", lineHeight:1.6 }}>
                      At current market prices (€{detail?.programme?.avg_price_per_vcu}/VCU),
                      full annual issuance of {fmtN(detail?.programme?.annual_reductions_tco2e)} credits
                      would generate <strong>{fmtEur((detail?.programme?.annual_reductions_tco2e||0) * (detail?.programme?.avg_price_per_vcu||15))}</strong> per year.
                      {detail?.programme?.article6_ready && " Article 6 ITMO premium could add 15–25% to base price."}
                    </p>
                  </div>
                </div>
              )}

              {/* AI Insights */}
              {tab === "ai" && (
                <div>
                  {(detail?.ai_insights||[]).map((insight, i) => (
                    <div key={i} style={{ background:"#060f1e", borderRadius:10, padding:"10px 14px", marginBottom:8,
                      borderLeft:`3px solid ${["#34d399","#f59e0b","#60a5fa"][i]||"#475569"}` }}>
                      <p style={{ fontSize:10, color:"#94a3b8", lineHeight:1.6 }}>{insight}</p>
                    </div>
                  ))}
                  <div style={{ marginTop:12 }}>
                    <Btn color="#34d399" onClick={generateAi} disabled={generating}>
                      {generating ? "Generating…" : "Generate Full AI Analysis"}
                    </Btn>
                  </div>
                  {aiContent && (
                    <pre style={{ marginTop:12, background:"#060f1e", borderRadius:10, padding:"12px 14px",
                      fontSize:9, color:"#94a3b8", lineHeight:1.6, whiteSpace:"pre-wrap",
                      fontFamily:"monospace", maxHeight:300, overflowY:"auto" }}>
                      {aiContent}
                    </pre>
                  )}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── Aggregation Engine ────────────────────────────────────────────────────────
function AggregationEngine({ onProgrammeCreated }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(null);
  const [created, setCreated] = useState(null);
  const [filterType, setFilterType] = useState("all");

  useEffect(() => {
    dbnApi.poaAggregation()
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleCreate = async (suggestion) => {
    setCreating(suggestion.id);
    try {
      const r = await dbnApi.createPoa({
        name: suggestion.title,
        credit_type: suggestion.credit_type.split(" ")[0],
        methodology: suggestion.methodology,
        registry: suggestion.recommended_registry,
        eligible_projects: suggestion.eligible_projects,
      });
      setCreated(r.data?.id);
      onProgrammeCreated && onProgrammeCreated();
    } catch(e) {}
    setCreating(null);
  };

  if (loading) return <LoadingState />;

  return (
    <div>
      <div style={{ background:"#001a0a", border:"1px solid #004a1e", borderRadius:10, padding:"10px 14px", marginBottom:16 }}>
        <p style={{ fontSize:10, color:"#34d399", fontWeight:700, marginBottom:4 }}>AI Aggregation Engine</p>
        <p style={{ fontSize:9, color:"#065f46", lineHeight:1.6 }}>
          The DeCarbonIQ Aggregation Engine analyses the portfolio for projects that share sufficient
          technology, methodology and geography to qualify for aggregation into a Programme of Activities.
          Currently <strong>{data?.eligible_not_aggregated}</strong> carbon/plastic-eligible projects are not yet enrolled in a PoA.
        </p>
      </div>

      <div style={{ display:"flex", gap:6, marginBottom:14 }}>
        {["all","Carbon","Plastic"].map(t => (
          <button key={t} onClick={() => setFilterType(t)}
            style={{ padding:"5px 12px", borderRadius:7, fontSize:9, fontWeight:700, border:"none", cursor:"pointer",
              background:filterType===t?"rgba(52,211,153,0.15)":"#0a1628",
              color:filterType===t?"#34d399":"#475569" }}>
            {t === "all" ? "All Types" : t}
          </button>
        ))}
        <span style={{ fontSize:10, color:"#475569", alignSelf:"center" }}>
          {data?.suggestions?.length} suggestions
        </span>
      </div>

      {(data?.suggestions||[]).filter(s => filterType==="all" || s.credit_type.includes(filterType)).map(s => (
        <div key={s.id} style={{ background:"#0a1628", border:`1px solid ${s.status==="Ready"?"#34d399":"#0f2033"}`,
          borderRadius:12, padding:"14px 16px", marginBottom:10 }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:8 }}>
            <div style={{ flex:1 }}>
              <p style={{ fontSize:12, fontWeight:700, color:"#f0f9ff", marginBottom:4 }}>{s.title}</p>
              <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
                <Badge label={s.credit_type} color={TYPE_COLORS[s.credit_type.split(" ")[0]]||"#475569"} />
                <Badge label={s.recommended_registry} color="#334155" />
                <Badge label={s.status} color={s.status==="Ready"?"#22c55e":s.status==="Review Recommended"?"#f59e0b":"#475569"} />
                <span style={{ fontSize:8, color:"#475569" }}>Confidence: {s.confidence_pct}%</span>
              </div>
            </div>
            <div style={{ textAlign:"right", flexShrink:0, marginLeft:12 }}>
              <p style={{ fontSize:14, fontWeight:900, color:"#34d399" }}>
                {fmtN(s.annual_reduction_potential_tco2e)} tCO₂e/yr
              </p>
              <p style={{ fontSize:8, color:"#475569" }}>{s.eligible_projects?.length} eligible projects</p>
            </div>
          </div>
          <p style={{ fontSize:9.5, color:"#64748b", lineHeight:1.6, marginBottom:10 }}>{s.rationale}</p>
          <div style={{ display:"flex", gap:6 }}>
            {s.eligible_projects?.slice(0,5).map(pid => (
              <span key={pid} style={{ fontSize:8, background:"#1e3a5f", color:"#60a5fa",
                padding:"1px 6px", borderRadius:4 }}>{pid}</span>
            ))}
            {(s.eligible_projects?.length||0) > 5 && (
              <span style={{ fontSize:8, color:"#334155" }}>+{s.eligible_projects.length-5} more</span>
            )}
          </div>
          <div style={{ marginTop:10, display:"flex", gap:8 }}>
            {s.id !== "AGG-DYN" && s.status === "Ready" && (
              <Btn color="#34d399" small onClick={() => handleCreate(s)} disabled={creating===s.id}>
                {creating===s.id ? "Creating…" : "Create Programme from This"}
              </Btn>
            )}
            {s.id === "AGG-DYN" && (
              <Btn color="#a78bfa" small onClick={() => handleCreate(s)} disabled={creating===s.id}>
                {creating===s.id ? "Creating…" : "Create New Programme"}
              </Btn>
            )}
          </div>
        </div>
      ))}

      {created && (
        <div style={{ background:"#001a0a", border:"1px solid #004a1e", borderRadius:10, padding:"10px 14px", marginTop:12 }}>
          <p style={{ fontSize:10, color:"#34d399" }}>Programme <strong>{created}</strong> created successfully. View it in the Programme Portfolio.</p>
        </div>
      )}

      {/* Eligible projects */}
      {(data?.eligible_projects||[]).length > 0 && (
        <div style={{ marginTop:16, background:"#0a1628", border:"1px solid #0f2033", borderRadius:12, padding:14 }}>
          <SectionHeader title="Eligible Projects — Not Yet Enrolled" sub={`${data.eligible_not_aggregated} projects ready for aggregation`} />
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:6 }}>
            {data.eligible_projects.slice(0,8).map(p => (
              <div key={p.id} style={{ background:"#060f1e", borderRadius:8, padding:"7px 10px" }}>
                <p style={{ fontSize:9.5, fontWeight:600, color:"#f0f9ff" }}>{p.name.slice(0,35)}</p>
                <div style={{ display:"flex", gap:4, marginTop:3 }}>
                  <span style={{ fontSize:8, color:"#64748b" }}>{p.sector} · {p.state}</span>
                </div>
                <div style={{ display:"flex", gap:4, marginTop:2 }}>
                  {p.carbon_eligible ? <Badge label="Carbon" color="#34d399" /> : null}
                  {p.plastic_eligible ? <Badge label="Plastic" color="#60a5fa" /> : null}
                  <span style={{ fontSize:8, color:"#475569" }}>{fmtN(p.credits_annual)} credits/yr</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── AI PoA Advisor ────────────────────────────────────────────────────────────
const POA_WORKFLOWS = [
  { id:"portfolio_summary",         label:"Portfolio Summary",          desc:"Executive summary of all PoAs, CPAs, issuance and revenue." },
  { id:"aggregation_recommendations",label:"Aggregation Recommendations",desc:"Which projects should be aggregated and why." },
  { id:"verification_risks",        label:"Verification Risk Analysis",  desc:"MRV gaps, verification bottlenecks, at-risk programmes." },
  { id:"revenue_forecast",          label:"5-Year Revenue Forecast",     desc:"Revenue projection across all PoAs with price assumptions." },
  { id:"article6_readiness",        label:"Article 6 Readiness",         desc:"Which PoAs qualify for ITMOs and the pathway to authorisation." },
];

function AIPoAAdvisor() {
  const [selected, setSelected] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [output, setOutput] = useState(null);
  const [copied, setCopied] = useState(false);

  const generate = async () => {
    if (!selected) return;
    setGenerating(true); setOutput(null);
    try {
      const r = await dbnApi.poaAi({ workflow: selected.id });
      setOutput(r.data);
    } catch(e) {}
    setGenerating(false);
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(output?.content||"");
    setCopied(true); setTimeout(()=>setCopied(false),2000);
  };

  return (
    <div style={{ display:"grid", gridTemplateColumns:"1fr 1.5fr", gap:16 }}>
      <div>
        <p style={{ fontSize:11, fontWeight:700, color:"#64748b", textTransform:"uppercase",
          letterSpacing:"0.1em", marginBottom:10 }}>AI Advisory Workflows</p>
        <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
          {POA_WORKFLOWS.map(w => (
            <div key={w.id} onClick={() => { setSelected(w); setOutput(null); }}
              style={{ background:selected?.id===w.id?"rgba(52,211,153,0.08)":"#0a1628",
                border:`1px solid ${selected?.id===w.id?"#34d399":"#0f2033"}`,
                borderRadius:10, padding:"10px 14px", cursor:"pointer", transition:"all .15s" }}>
              <p style={{ fontSize:11, fontWeight:700, color:"#f0f9ff", marginBottom:3 }}>{w.label}</p>
              <p style={{ fontSize:9, color:"#475569", lineHeight:1.4 }}>{w.desc}</p>
            </div>
          ))}
        </div>
      </div>
      <div>
        {selected ? (
          <div style={{ background:"#0a1628", border:"1px solid #0f2033", borderRadius:14, padding:16 }}>
            <p style={{ fontSize:13, fontWeight:800, color:"#f0f9ff", marginBottom:8 }}>{selected.label}</p>
            <p style={{ fontSize:10, color:"#64748b", marginBottom:14, lineHeight:1.5 }}>{selected.desc}</p>
            <Btn color="#34d399" onClick={generate} disabled={generating}>
              {generating ? "Generating…" : "Generate Analysis"}
            </Btn>
            {generating && (
              <div style={{ textAlign:"center", padding:"24px 0" }}>
                <div style={{ width:24, height:24, border:"3px solid #0f2033", borderTopColor:"#34d399",
                  borderRadius:"50%", animation:"spin 1s linear infinite", margin:"0 auto 10px" }} />
                <p style={{ fontSize:10, color:"#475569" }}>Analysing PoA portfolio…</p>
              </div>
            )}
            {output && (
              <div style={{ marginTop:14 }}>
                <div style={{ display:"flex", justifyContent:"space-between", marginBottom:8 }}>
                  <Badge label="Generated" color="#34d399" />
                  <div style={{ display:"flex", gap:6 }}>
                    <Btn small outline color="#475569" onClick={handleCopy}>{copied?"Copied!":"Copy"}</Btn>
                    <Btn small outline color="#60a5fa" onClick={() => {
                      const a=document.createElement("a");
                      a.href=URL.createObjectURL(new Blob([output.content],{type:"text/plain"}));
                      a.download=`PoA-${selected.id}-${new Date().toISOString().slice(0,10)}.txt`;
                      a.click();
                    }}>Download</Btn>
                  </div>
                </div>
                <pre style={{ background:"#060f1e", borderRadius:10, padding:"12px 14px",
                  fontSize:9, color:"#94a3b8", lineHeight:1.6, whiteSpace:"pre-wrap",
                  fontFamily:"monospace", maxHeight:380, overflowY:"auto", border:"1px solid #0f2033" }}>
                  {output.content}
                </pre>
              </div>
            )}
          </div>
        ) : (
          <div style={{ background:"#0a1628", border:"1px solid #0f2033", borderRadius:14, padding:32,
            display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", textAlign:"center" }}>
            <Bot size={40} color="#1e3a5f" style={{ marginBottom:12 }} />
            <p style={{ fontSize:13, color:"#334155" }}>Select a workflow to generate AI analysis</p>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main PoA Workspace ────────────────────────────────────────────────────────
export default function DBNPoA() {
  const [view, setView] = useState("overview");
  const [overview, setOverview] = useState(null);
  const [programmes, setProgrammes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPoa, setSelectedPoa] = useState(null);
  const [reportGenerating, setReportGenerating] = useState(null);

  const load = useCallback(() => {
    Promise.all([dbnApi.poaOverview(), dbnApi.poaProgrammes()])
      .then(([r1, r2]) => {
        setOverview(r1.data);
        setProgrammes(r2.data?.programmes || []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleReport = (type, fmt) => {
    setReportGenerating(`${type}-${fmt}`);
    window.open(dbnApi.poaReportDownload(type, fmt), "_blank");
    setTimeout(() => setReportGenerating(null), 2500);
  };

  if (loading) return <LoadingState label="Loading PoA portfolio…" />;

  const k = overview?.kpis || {};

  return (
    <div>
      {/* PoA Summary bar */}
      <div style={{ background:"linear-gradient(135deg,#07111e,#0a1e36)", border:"1px solid #1e3a5f",
        borderRadius:12, padding:"12px 16px", marginBottom:14 }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
          <div>
            <p style={{ fontSize:13, fontWeight:800, color:"#f0f9ff" }}>Programme of Activities (PoA)</p>
            <p style={{ fontSize:10, color:"#475569" }}>
              Carbon & Environmental Credits · {k.total_poas} programmes · {k.total_cpas} CPAs · {k.states_covered} states
            </p>
          </div>
          <div style={{ display:"flex", gap:6 }}>
            {["pdf","excel","word","ppt"].map(fmt => (
              <button key={fmt} onClick={() => handleReport("portfolio-summary", fmt)}
                data-testid={`poa-report-${fmt}`}
                style={{ padding:"4px 10px", borderRadius:6, fontSize:8.5, fontWeight:700, cursor:"pointer",
                  background:"transparent", border:`1px solid #1e3a5f`, color:"#475569",
                  transition:"all .15s" }}
                onMouseEnter={e=>e.currentTarget.style.borderColor="#34d399"}
                onMouseLeave={e=>e.currentTarget.style.borderColor="#1e3a5f"}>
                {reportGenerating===`portfolio-summary-${fmt}` ? "…" : fmt.toUpperCase()}
              </button>
            ))}
          </div>
        </div>
        <div style={{ display:"flex", gap:12, marginTop:10 }}>
          {[
            {l:"Annual tCO₂e",    v:fmtN(k.annual_tco2e),          c:"#34d399"},
            {l:"Credits Issued",  v:fmtN(k.credits_issued),         c:"#fbbf24"},
            {l:"Revenue Realised",v:fmtEur(k.revenue_realised),     c:"#22c55e"},
            {l:"Pipeline",        v:fmtEur(k.revenue_pipeline),     c:"#a78bfa"},
            {l:"Article 6 Ready", v:programmes.filter(p=>p.article6_ready).length + " PoAs", c:"#60a5fa"},
          ].map(x => (
            <div key={x.l} style={{ background:"rgba(255,255,255,0.04)", borderRadius:7, padding:"6px 12px",
              borderTop:`2px solid ${x.c}` }}>
              <p style={{ fontSize:13, fontWeight:900, color:x.c }}>{x.v}</p>
              <p style={{ fontSize:7.5, color:"#334155", textTransform:"uppercase" }}>{x.l}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Sub-nav */}
      <div style={{ display:"flex", gap:2, marginBottom:14, background:"#0a1628",
        border:"1px solid #0f2033", borderRadius:10, padding:4, width:"fit-content" }}>
        {[
          {key:"overview",     label:"Executive Dashboard"},
          {key:"programmes",   label:"Programme Portfolio"},
          {key:"aggregation",  label:"Aggregation Engine"},
          {key:"advisor",      label:"AI PoA Advisor"},
        ].map(t => (
          <button key={t.key} onClick={() => setView(t.key)}
            style={{ padding:"7px 14px", borderRadius:8, border:"none", cursor:"pointer", fontSize:11, fontWeight:700,
              background:view===t.key?"rgba(52,211,153,0.15)":"transparent",
              color:view===t.key?"#34d399":"#475569", whiteSpace:"nowrap" }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Content */}
      {view === "overview"    && <PoADashboard overview={overview} />}
      {view === "programmes"  && <ProgrammePortfolio programmes={programmes} onSelect={setSelectedPoa} />}
      {view === "aggregation" && <AggregationEngine onProgrammeCreated={load} />}
      {view === "advisor"     && <AIPoAAdvisor />}

      {/* Programme detail drawer */}
      {selectedPoa && (
        <ProgrammeDetail poaId={selectedPoa} onClose={() => setSelectedPoa(null)} />
      )}
    </div>
  );
}
